import { createContext, useContext, useCallback, useEffect, useState, type ReactNode } from 'react'
import type { PortalUserInfo } from '@/types'

// ── Types ──────────────────────────────────────────────────

export interface FirmaInfo {
  D365_ContactID: string
  Firmenname: string
  D365_AccountID: string
  token: string
}

interface AuthState {
  user: PortalUserInfo | null
  firmen: FirmaInfo[]
  isLoading: boolean
  isAuthenticated: boolean
  isAdmin: boolean
  isSuperAdmin: boolean
}

interface AuthContextValue extends AuthState {
  login: (email: string) => Promise<{ step: string; message: string }>
  setUserFromResponse: (user: PortalUserInfo, firmen?: FirmaInfo[]) => void
  logout: () => Promise<void>
  refresh: () => Promise<void>
  /** Firma-Token für eine AccountID finden. */
  getFirmaToken: (accountId: string) => string | undefined
}

const AuthContext = createContext<AuthContextValue | null>(null)

// ── Provider ───────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PortalUserInfo | null>(null)
  const [firmen, setFirmen] = useState<FirmaInfo[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Beim Start: Prüfen ob der User noch eingeloggt ist (via Cookie)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        let res = await fetch('/api/auth/me')
        if (res.status === 401) {
          const refreshRes = await fetch('/api/auth/refresh', { method: 'POST' })
          if (refreshRes.ok) {
            res = await fetch('/api/auth/me')
          }
        }
        if (res.ok) {
          const data = await res.json()
          setUser(data.user)
          if (data.firmen) setFirmen(data.firmen)
        }
      } catch {
        // Nicht eingeloggt – OK
      } finally {
        setIsLoading(false)
      }
    }
    checkAuth()
  }, [])

  const login = useCallback(async (email: string): Promise<{ step: string; message: string }> => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({ detail: 'Login fehlgeschlagen' }))
      throw new Error(data.detail || `Fehler ${res.status}`)
    }
    const data = await res.json()
    return { step: data.step, message: data.message }
  }, [])

  const setUserFromResponse = useCallback((userInfo: PortalUserInfo, firmenList?: FirmaInfo[]) => {
    setUser(userInfo)
    if (firmenList) setFirmen(firmenList)
  }, [])

  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' })
    } catch {
      // Ignore
    }
    setUser(null)
    setFirmen([])
  }, [])

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/refresh', { method: 'POST' })
      if (res.ok) {
        const data = await res.json()
        setUser(data.user)
      } else {
        setUser(null)
      }
    } catch {
      setUser(null)
      setFirmen([])
    }
  }, [])

  const getFirmaToken = useCallback((accountId: string): string | undefined => {
    return firmen.find(f => f.D365_AccountID?.toLowerCase() === accountId?.toLowerCase())?.token
  }, [firmen])

  const value: AuthContextValue = {
    user,
    firmen,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: user?.Role === 'admin' || user?.Role === 'superadmin',
    isSuperAdmin: user?.Role === 'superadmin',
    login,
    setUserFromResponse,
    logout,
    refresh,
    getFirmaToken,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// ── Hooks ──────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth muss innerhalb von AuthProvider verwendet werden')
  return ctx
}
